'use client'

import { Container } from '@ccpgames/eve-web-core'
import { useRef, useState } from 'react'
import Image from 'next/image'
import classes from './ImageGallery.module.css'
import NextJsImage from './NextJsImage'
import Lightbox from 'yet-another-react-lightbox'
import Thumbnails from 'yet-another-react-lightbox/plugins/thumbnails'
import Download from 'yet-another-react-lightbox/plugins/download'
import 'yet-another-react-lightbox/styles.css'
import 'yet-another-react-lightbox/plugins/thumbnails.css'
import { Heading } from '@/components/shared/typography'
import { FadeInSlideUp } from '@/components/shared/animations'
import { Paragraph } from '@/components/shared/typography'
import { ImageGalleryFieldsFragment } from '@/graphql/types'

interface ImageGallerySectionProps {
  imageGallery: ImageGalleryFieldsFragment
}

export const ImageGallerySection = ({ imageGallery }: ImageGallerySectionProps) => {
  const imagesCollection = imageGallery.imagesCollection?.items ?? []
  const columnCount = imageGallery.columnCount ?? 4

  const [open, setOpen] = useState(false)
  const [index, setIndex] = useState(-1)
  const thumbnailsRef = useRef(null)

  const slides = imagesCollection
    .filter((image) => image && image.url)
    .map((image) => ({
      src: image?.url + '?fm=webp&w=2400&q=75',
    }))

  const imageLoader = ({ src, width, quality }) => {
    return `${src}?w=${width}&q=${quality || 75}`
  }

  return (
    <div className={classes.galleryWrapper}>
      {(imageGallery.title || imageGallery.description) && (
        <Container>
          {imageGallery.title && (
            <Heading
              color="#fff"
              responsive={{ maxSize: 36, minSize: 24 }}
              uppercase
              variant="semi-bold-36"
              as="h4"
              className={classes.title}
            >
              {imageGallery.title}
            </Heading>
          )}
          {imageGallery.description && (
            <Paragraph variant="regular-22" responsive={{ maxSize: 22, minSize: 18 }} className={classes.description}>
              {imageGallery.description}
            </Paragraph>
          )}
        </Container>
      )}

      <div className={classes.lightBoxWrapper}>
        <Lightbox
          open={open}
          close={() => setOpen(false)}
          index={index}
          className={classes.lightboxOverride}
          slides={slides}
          thumbnails={{ ref: thumbnailsRef }}
          plugins={[Thumbnails, Download]}
          render={{
            slide: NextJsImage,
            // iconNext: () => <>Next</>,
            // iconPrev: () => <>Prev</>,
          }}
        />

        <div
          className={classes.imageWrapper}
          style={{
            ['--column-count' as string]: columnCount,
            gridTemplateColumns: `repeat(${columnCount}, 1fr)`,
          }}
        >
          {imagesCollection.map((image, index) => {
            if (!image || !image.url) {
              return null
            }
            return (
              <FadeInSlideUp key={index} delay={index * 0.1}>
                <div
                  key={index}
                  className={classes.imageContainer}
                  onClick={() => {
                    setOpen(true)
                    setIndex(index)
                  }}
                >
                  <Image
                    src={imageLoader({ src: image.url, width: 480, quality: 75 })}
                    alt=""
                    fill
                    style={{
                      objectFit: 'cover',
                    }}
                  />
                </div>
              </FadeInSlideUp>
            )
          })}
        </div>
      </div>
    </div>
  )
}
